/**
 * Profit compute — the single source of truth for MRR / margin math shared by
 * the actuals views and the what-if simulator.
 *
 * The backend (ReportingProfitService) computes the actuals; this module mirrors
 * the exact same formula so that, when the simulator overrides a SKU's buy/sell
 * unit price, the recomputed figures stay consistent with the server baseline.
 * Everything is GST-exclusive and normalized to Monthly Recurring Revenue (MRR):
 * an annual side is divided by 12.
 */

import type { Cadence, ProfitBaselineRow } from '@techgitter/contracts';

/** Per-SKU price overrides entered in the simulator. Buy and sell are overridden
 *  per pricing model (month-to-month commitment vs annual commitment); a row
 *  picks the model matching its subscription's commitment. Absent fields fall
 *  back to the baseline row's own unit prices. */
export interface SkuOverride {
  /** Overrides for monthly-commitment subscriptions. */
  buyM2mExTax?: number | null;
  sellM2mExTax?: number | null;
  /** Overrides for annual-commitment subscriptions. */
  buyAnnualExTax?: number | null;
  sellAnnualExTax?: number | null;
}

/** The pricing model a baseline row belongs to — its subscription's commitment
 *  (falling back to the buy/sell cadence when there's no subscription). */
export function modelOf(row: ProfitBaselineRow): Cadence {
  return row.subscriptionCadence ?? row.buyCadence ?? row.sellCadence ?? 'monthly';
}

export type OverrideMap = Record<string, SkuOverride>;

/** A baseline row with buy/sell unit prices (and the figures derived from them)
 *  replaced by the active simulator overrides for its SKU. */
export interface ComputedProfitRow extends ProfitBaselineRow {
  /** True when an override changed this row's buy and/or sell unit price. */
  overridden: boolean;
}

function monthly(amount: number | null, cadence: Cadence | null): number | null {
  if (amount === null) return null;
  return cadence === 'annual' ? amount / 12 : amount;
}

function round(value: number | null, places: number): number | null {
  if (value === null) return null;
  const f = 10 ** places;
  return Math.round(value * f) / f;
}

/**
 * Recompute a row's MRR / profit / margin from (optionally overridden) unit
 * prices. The cost seat basis is the committed subscription seats, falling back
 * to billed seats when there is no subscription — matching the server.
 */
export function computeProfitRow(row: ProfitBaselineRow, override?: SkuOverride): ComputedProfitRow {
  // Pick the buy/sell overrides matching this subscription's commitment model.
  const isAnnual = modelOf(row) === 'annual';
  const buyOverride = isAnnual ? override?.buyAnnualExTax : override?.buyM2mExTax;
  const sellOverride = isAnnual ? override?.sellAnnualExTax : override?.sellM2mExTax;
  const buyUnit = buyOverride != null ? buyOverride : row.buyUnitPriceExTax;
  const sellUnit = sellOverride != null ? sellOverride : row.sellUnitPriceExTax;

  const overridden =
    (buyOverride != null && buyOverride !== row.buyUnitPriceExTax) ||
    (sellOverride != null && sellOverride !== row.sellUnitPriceExTax);

  const buySeats = row.seats > 0 ? row.seats : row.billedSeats;
  const mrrBuy = buyUnit !== null ? monthly(buyUnit * buySeats, row.buyCadence) : null;
  const mrrSell = sellUnit !== null ? monthly(sellUnit * row.billedSeats, row.sellCadence) : null;
  const mrrProfit = mrrSell !== null && mrrBuy !== null ? mrrSell - mrrBuy : null;
  const marginPct = mrrProfit !== null && mrrSell ? mrrProfit / mrrSell : null;

  return {
    ...row,
    buyUnitPriceExTax: round(buyUnit, 4),
    sellUnitPriceExTax: round(sellUnit, 4),
    mrrBuy: round(mrrBuy, 2),
    mrrSell: round(mrrSell, 2),
    mrrProfit: round(mrrProfit, 2),
    marginPct: round(marginPct, 4),
    overridden,
  };
}

export function applyOverrides(rows: ProfitBaselineRow[], overrides: OverrideMap): ComputedProfitRow[] {
  return rows.map(row => computeProfitRow(row, row.sku ? overrides[row.sku] : undefined));
}

export interface ProfitTotals {
  mrrBuy: number;
  mrrSell: number;
  mrrProfit: number;
  /** Aggregate margin = total profit / total sell (null when no known sell). */
  marginPct: number | null;
  /** Rows with an unknown buy or sell side, excluded from the totals. */
  unknownRows: number;
}

/** Sum MRR across rows, skipping rows where either side is unknown (so the
 *  margin stays comparable). */
export function sumTotals(rows: ComputedProfitRow[]): ProfitTotals {
  let mrrBuy = 0;
  let mrrSell = 0;
  let unknownRows = 0;
  for (const r of rows) {
    if (r.mrrBuy === null || r.mrrSell === null) {
      unknownRows += 1;
      continue;
    }
    mrrBuy += r.mrrBuy;
    mrrSell += r.mrrSell;
  }
  const mrrProfit = mrrSell - mrrBuy;
  return {
    mrrBuy: round(mrrBuy, 2) ?? 0,
    mrrSell: round(mrrSell, 2) ?? 0,
    mrrProfit: round(mrrProfit, 2) ?? 0,
    marginPct: mrrSell ? round(mrrProfit / mrrSell, 4) : null,
    unknownRows,
  };
}

// ---------------------------------------------------------------------------
// Formatting helpers (shared by the tables)
// ---------------------------------------------------------------------------

export function fmtMoney(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  return value.toLocaleString('en-AU', { style: 'currency', currency: 'AUD' });
}

export function fmtPct(fraction: number | null | undefined): string {
  if (fraction === null || fraction === undefined) return '—';
  return `${(fraction * 100).toFixed(1)}%`;
}
